import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { WaitlistForm } from "@/components/WaitlistForm";

/** Full product card for the Products page: visual, copy, CTAs, and waitlist. */
export function ProductCard({ product }) {
  const { key, name, category, status, url, description, highlights, image } = product;

  return (
    <article
      id={key}
      data-testid={`product-detail-${key}`}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-line bg-white shadow-sm transition-[border-color,box-shadow] hover:border-royal/40 hover:shadow-lg scroll-mt-28"
    >
      <div className="relative h-52 overflow-hidden">
        <img
          src={image}
          alt={`${name} platform`}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-steel/90 via-steel/30 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-5">
          <span className="rounded border border-white/20 bg-white/10 px-2.5 py-1 font-mono-sub text-[11px] font-semibold uppercase tracking-wider text-white backdrop-blur">
            {category}
          </span>
          <span
            data-testid={`product-status-${key}`}
            className="rounded bg-royal px-2.5 py-1 font-mono-sub text-[11px] font-semibold text-white"
          >
            {status}
          </span>
        </div>
      </div>

      <div className="flex flex-1 flex-col p-7">
        <h2 className="font-poster text-2xl font-bold text-steel">{name}</h2>
        <p className="mt-3 text-sm leading-relaxed text-ink">{description}</p>

        <ul className="mt-5 flex flex-wrap gap-2" aria-label={`${name} highlights`}>
          {highlights.map((item) => (
            <li
              key={item}
              className="rounded border border-line bg-cloud px-2.5 py-1 font-mono-sub text-[11px] text-steel"
            >
              {item}
            </li>
          ))}
        </ul>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Link
            to="/contact"
            data-testid={`product-cta-${key}`}
            className="rounded bg-royal px-5 py-2.5 font-mono-sub text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-royal-dark"
          >
            Request a Consultation
          </Link>
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`product-link-${key}`}
              className="inline-flex items-center gap-1.5 rounded border border-slate-300 px-5 py-2.5 font-mono-sub text-xs font-bold uppercase tracking-wider text-slate-700 transition-colors hover:bg-cloud"
            >
              Visit product <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>

        <div className="mt-auto border-t border-line pt-6">
          <WaitlistForm productKey={key} productName={name} />
        </div>
      </div>
    </article>
  );
}
